import { Injectable } from '@angular/core';
import { formatDate } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { AttachmentBase } from '@sb-tools/typescript/models/attachment-base.model';
import { NewLinePipe } from '@sb-tools/typescript/pipes/new-line.pipe';
import { MessageThreadElementType } from '@sb-tools/typescript/enums/message-thread-element-type.enum';
import { AttachmentType } from '@sb-tools/typescript/enums/attachment-type.enum';
import { first } from 'rxjs/operators';
import { BsModalService } from 'ngx-bootstrap/modal';
import { AttachmentService } from '@sb-tools/typescript/services/attachment.service';
import { ForwardEntityTypeEnum } from '../enums/forward-entity-type.enum';
import { ThreadElementDTO } from '@sb-tools/typescript/models/dto/thread-element.dto';
import { DetailedTicketDTO } from '../../core/models/dto/detailed-ticket.dto';
import { ForwardEntity } from '../../core/models/forward-entity.model';
import { TicketDTO } from '../../core/models/dto/ticket.dto';
import { SatisfactionDTO } from '../../core/models/dto/satisfaction.dto';
import { SurveyDTO } from '../../core/models/dto/survey.dto';
import { ForwardMessageModalComponent } from '../../shared/modal-windows/forward-message-modal/forward-message-modal.component';

@Injectable({
    providedIn: 'root',
})
export class ForwardMessageService {
    maxVisibleSubrepliesLevel = 8;

    constructor(
        private translateService: TranslateService,
        private newLinePipe: NewLinePipe,
        private bsModalService: BsModalService,
        private attachmentService: AttachmentService,
    ) {}

    addInlineStylesToMessageHtmlString(messageString: string): string {
        const quillIndentUnit = 24;

        for (let i = 0; i <= this.maxVisibleSubrepliesLevel; i++) {
            messageString = messageString
                .replace(
                    new RegExp('<blockquote>', 'g'),
                    '<blockquote style="margin: 0; padding-left: 16px; border-left: 4px solid #ccc">',
                )
                .replace(
                    new RegExp(`<blockquote class="ql-indent-${i}"`, 'g'),
                    `<blockquote style="padding-left: 16px; margin-left: ${
                        quillIndentUnit * i
                    }px; border-left: 4px solid #ccc" class="ql-indent-${i}"`,
                )
                .replace(
                    new RegExp(`<div class="ql-indent-${i}"`, 'g'),
                    `<div style="padding-left: 16px; margin-left: ${quillIndentUnit * i}px;" class="ql-indent-${i}"`,
                );
        }

        return messageString;
    }

    getMessageValueForForwardThread(ticketData: DetailedTicketDTO, isSortByTimeDesc: boolean): string {
        const ticketMainMessageData: string =
            this.getMessageCreationDataString(ticketData, true) +
            (ticketData.message.html || ticketData.message.text) +
            this.getAttachmentNamesString(ticketData.attachments);
        const separator = '<p><br><br></p>';
        const repliesData = this.getTicketRepliesString(ticketData.messageThread);

        if (isSortByTimeDesc) {
            return separator + repliesData + (repliesData ? separator : '') + ticketMainMessageData;
        } else {
            return (
                separator + ticketMainMessageData + (ticketMainMessageData ? separator : '') + separator + repliesData
            );
        }
    }

    openForwardForm(
        messageData: ForwardEntity | DetailedTicketDTO | ThreadElementDTO,
        entityType: ForwardEntityTypeEnum,
        ticket: DetailedTicketDTO,
    ): void {
        const initialState = {
            entity: messageData,
            entityType,
            currentTicket: ticket,
        };

        this.bsModalService.show(ForwardMessageModalComponent, {
            initialState,
            class: 'modal-lg new-ticket-modal',
            ignoreBackdropClick: true,
        });

        this.bsModalService.onHide.pipe(first()).subscribe(() => {
            this.attachmentService.changeForwardingStatus(true);
        });
    }

    processMessageThread(messageThread: ThreadElementDTO[], isSortByTimeDesc: boolean): ThreadElementDTO[] {
        return [...this.getFilteredFromDraftsReplies(messageThread)].sort((a, b) => {
            const timeA = new Date(a.threadLastUpdateTime).getTime();
            const timeB = new Date(b.threadLastUpdateTime).getTime();
            return isSortByTimeDesc ? timeB - timeA : timeA - timeB;
        });
    }

    private getAttachmentNamesString(attachments: AttachmentBase[]): string {
        if (attachments.length) {
            let str = '<br>';

            for (const attachment of attachments) {
                str += `<span style="color: rgb(153, 153, 153)">&lt;!${
                    attachment.attachmentType === AttachmentType.carousel
                        ? this.translateService.instant('carousel')
                        : attachment.name || attachment.url
                }&gt;</span><br>`;
            }

            return str;
        } else {
            return '';
        }
    }

    private getFilteredFromDraftsReplies(replies: ThreadElementDTO[]): ThreadElementDTO[] {
        return replies.filter((reply) => {
            const filteredFromDraftReply = !reply.isDraft && reply.type === MessageThreadElementType.reply;

            if (filteredFromDraftReply && reply.subReplies) {
                reply.subReplies = this.getFilteredFromDraftsReplies(reply.subReplies);
            }
            return filteredFromDraftReply;
        });
    }

    private getMessageCreationDataString(entity: DetailedTicketDTO | ThreadElementDTO, isTicket: boolean): string {
        if (isTicket) {
            return `<span style="color: rgb(153, 153, 153);font-size: 13px;">${this.translateService.instant(
                'From:',
            )} ${(entity as TicketDTO).firstMessageCreator.name} ${
                (entity as TicketDTO).firstMessageCreator.email
                    ? `&lt;<a href="mailto: ${
                          (entity as TicketDTO).firstMessageCreator.email
                      }" style="color: rgb(64, 151, 255);" target="_blank">${
                          (entity as TicketDTO).firstMessageCreator.email
                      }</a>&gt;`
                    : ''
            } </span><br><span style="color: rgb(153, 153, 153);">${this.translateService.instant(
                'Date:',
            )} ${formatDate((entity as TicketDTO).firstMessageCreationTime, 'full', 'en-US')}</span><br>`;
        } else {
            return `<span style="color: rgb(153, 153, 153);font-size: 13px;">On ${formatDate(
                (entity as ThreadElementDTO).creationTime,
                'medium',
                'en-US',
            )}, ${(entity as ThreadElementDTO).author.name} ${this.translateService.instant('wrote:')}</span><br>`;
        }
    }

    private getSubrepliesListItemString(subReply: ThreadElementDTO): string {
        return `<div class="ql-indent-${subReply.level}">${this.getMessageCreationDataString(subReply, false)}</div>
            <blockquote class="ql-indent-${subReply.level}">${this.newLinePipe.transform(
                subReply.message.html || subReply.message.text,
            )} ${this.getAttachmentNamesString(subReply.attachments)}</blockquote><br>`;
    }

    private getSubrepliesListString(str: string, reply: ThreadElementDTO): string {
        for (const subReply of reply.subReplies) {
            str += this.getSubrepliesListItemString(subReply);

            if (subReply.level === this.maxVisibleSubrepliesLevel && subReply.subReplies?.length) {
                str += `<div class="ql-indent-${this.maxVisibleSubrepliesLevel}"><a href="${subReply.permalink}">
                ${this.translateService.instant('see all sub replies in the origin thread')}</a></div><br>`;
            } else {
                if (subReply.subReplies?.length) {
                    str = this.getSubrepliesListString(str, subReply);
                }
            }
        }

        return str;
    }

    private getTicketRepliesString(thread: (ThreadElementDTO | SatisfactionDTO | SurveyDTO)[]): string {
        if (thread.length) {
            let str = '';
            for (const reply of thread as ThreadElementDTO[]) {
                str += `${this.getMessageCreationDataString(reply, false)}<blockquote>${
                    reply.message.html || reply.message.text
                } ${this.getAttachmentNamesString(reply.attachments)} </blockquote><br>${
                    reply.subReplies?.length ? this.getSubrepliesListString('', reply) + '<br>' : '<br>'
                }`;
            }
            return str;
        }
        return '';
    }
}
